// SPDX-License-Identifier: GPL-3.0-or-later
// The fixed look of the bursts. Three tiers for a contact (a weak, a middle and a strong hit),
// one for a halo and one for a light show. A tier is picked by the strength of the event, so an
// event makes no new object.

import type { BurstShape } from "./particles.ts";

/** Contact bursts by strength tier: weak, middle, strong. */
export const CONTACT_SHAPES: readonly [BurstShape, BurstShape, BurstShape] = [
  { speed: 180, size: 8, endSize: 3, lifeMs: 450, gravity: 700, lift: 0.6 },
  { speed: 300, size: 10, endSize: 3, lifeMs: 560, gravity: 700, lift: 0.7 },
  { speed: 440, size: 13, endSize: 4, lifeMs: 700, gravity: 700, lift: 0.8 },
];

/** A halo: a big soft disc that swells and fades. It does not move. */
export const HALO_SHAPE: BurstShape = {
  speed: 0,
  size: 60,
  endSize: 150,
  lifeMs: 380,
  gravity: 0,
  lift: 0,
};

/** A big halo for a table moment. */
export const BIG_HALO_SHAPE: BurstShape = {
  speed: 0,
  size: 80,
  endSize: 200,
  lifeMs: 450,
  gravity: 0,
  lift: 0,
};

/** A burst of the light show. It is small and short, like a strong contact burst. */
export const SHOW_SHAPE: BurstShape = {
  speed: 440,
  size: 13,
  endSize: 4,
  lifeMs: 700,
  gravity: 500,
  lift: 0.85,
};

/** Particles of a contact burst at strength 0 and at strength 1. */
export const COUNT_MIN = 6;
export const COUNT_MAX = 28;
/** Particles of one burst of the light show. */
export const SHOW_COUNT = 12;

/** The tier of a strength from 0 to 1. A value that is not a number gives the middle tier. */
export function tierOf(strength: number): 0 | 1 | 2 {
  if (Number.isNaN(strength)) return 1;
  if (strength < 0.34) return 0;
  return strength < 0.67 ? 1 : 2;
}

/** The particle count of a contact for a strength and a budget from 0 to 1. */
export function contactCount(strength: number, budget: number): number {
  const s = Number.isFinite(strength) ? Math.min(1, Math.max(0, strength)) : 0.5;
  return Math.round((COUNT_MIN + (COUNT_MAX - COUNT_MIN) * s) * budget);
}
